import { Injectable } from "@angular/core";
import { BACKEND_URI } from "../config/config-vars";
import { HttpClient, HttpParams } from "@angular/common/http";
import { GameService } from "./game.service";
import { LoginService } from "./login.service";
import { Board } from "../models/board.model";
import { firstValueFrom, map } from "rxjs";
import { BoardStats } from "../models/boardStats.model";
import { bind } from "leaflet";
import { SalesRep } from "../models/sales-rep.model";

const BASE_URL = `${BACKEND_URI}/api/boardStats`;
const GET_ALL_URL = `${BASE_URL}`;
const INSERT_URL = BASE_URL;
const UPLOAD_FILES_URL = `${BASE_URL}/file/upsert`;
const UPDATE_URL = (id) => `${BASE_URL}/${encodeURIComponent(id)}`;
const DELETE_ROW_BY_SERIAL_URL = `${BASE_URL}/admin/row-by-serial`;
const GET_BY_ID_URL = (id) => `${BASE_URL}/${encodeURIComponent(id)}`;
const DELETE_BY_ID_URL = (id) => `${BASE_URL}/${encodeURIComponent(id)}`;
const DOWNLOAD_FILES_URL = `${BASE_URL}/file/download`;
const GET_LATEST_BOARD_ID_URL = (boardId: string) => `${BASE_URL}/latest/${boardId}`;
const GET_DASHBOARD_SUMMARY_URL = `${BASE_URL}/dashboard-summary`;
const GET_DASHBOARD_CATEGORY_SUMMARIES_URL = `${BASE_URL}/dashboard-category-summaries`;
const formatLocalDateTime = (date: Date): string => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};
const GET_ALL_BY_DATE_RANGE_URL = (
  startDate: Date,
  endDate: Date,
  salesRepFirstName?: string,
  salesRepLastName?: string
) => {
  const params: { [key: string]: string | undefined } = {
    startDate: startDate ? formatLocalDateTime(startDate) : undefined,
    endDate: endDate ? formatLocalDateTime(endDate) : undefined,
    salesRepFirstName,
    salesRepLastName,
  };

  const queryParams = new URLSearchParams(
    Object.entries(params).reduce((acc, [key, value]) => {
      if (value) acc[key] = value; // Only include parameters with defined values
      return acc;
    }, {} as { [key: string]: string })
  ).toString();

  return `${BASE_URL}?${queryParams}`;
};

export interface GroupedStats {
  avgDailyCashIn: number;
  avgDailyNet: number;
  avgDailyCoinIn: number,
  avgDailyCoinOut: number,
  avgDailyGamePlayed: number,
  avgDailyGameWon: number,
  avgDays: number,
  sumDailyCashIn: number;
  sumDailyNet: number;
  sumDailyCoinIn: number,
  sumDailyCoinOut: number,
  sumDailyGamePlayed: number,
  sumDailyGameWon: number,
  totalCashin: number,
  totalCashout: number,
  totalCoinIn:number,
  totalCoinOut: number,
  totalGamesPlayed: number,
  totalGamesWon: number,
  totalNet: number,
  totalDays: number,
  winRatio: number,
  payoutRatio: number,
  rpt: number
  betMultiplier: number,
  betPerPlay: number,  
  count:  number;
  rawStats?: BoardStats[]; // <-- Add this
}

export interface DashboardBoardStatsSummary {
  averageCashInPerDay: number;
  averageNetPerDay: number;
  daysSincePrevDisplay: number;
  validStatsCount: number;
  flaggedStatsCount: number;
}

@Injectable({ providedIn: 'root' })
export class BoardStatService {
 

  standardGameNames = {
    RED_BLAZE: 'RED BLAZE',
    GOLD_LINK: 'GOLD LINK',
    KLASSIC_KRAZE: 'KLASSIC KRAZE',
    DIAMOND_BLAST_LINK: 'DIAMOND BLAST LINK',
    SILVER_LINK: 'SILVER LINK',
    PLATINUM_BLITZ: 'PLATINUM BLITZ',
    BUFFALO_RUSH: 'BUFFALO RUSH',
    KLASSIC_LINK: 'KLASSIC LINK',
    GOLDEN_CITY: 'GOLDEN_CITY',
    FORTUNE_IP: 'FORTUNE IP',
    FORTUNE: 'FORTUNE'
  };


  constructor(private http: HttpClient, private loginService: LoginService) { }

  async getAll(): Promise<BoardStats[]> {
    console.log('Fetching data from:', GET_ALL_URL);

    if (this.loginService.isloggedIn) {
      try {
        const response = await firstValueFrom(
          this.http.get<BoardStats[]>(GET_ALL_URL)
        );
        console.log('Data fetched:', response);
        return response;
      } catch (error) {
        console.error('Error fetching data:', error);
        return [];
      }
    } else {
      console.warn('User is not logged in');
      return [];
    }
  }

  private standardizeGameName(gameName: string): string {
    if (!gameName) return '';
    
    const name = gameName.toUpperCase();
    if (name.includes('red')) {
      return this.standardGameNames.RED_BLAZE;
    } else if (name.includes('gold')) {
      return this.standardGameNames.GOLD_LINK;
    } else if (name.includes('classic') || name.includes('klassic')) {
      return this.standardGameNames.KLASSIC_KRAZE;
    } else if (name.includes('diamond')) {
      return this.standardGameNames.DIAMOND_BLAST_LINK;
    } else if (name.includes('silver')) {
      return this.standardGameNames.SILVER_LINK;
    } else if (name.includes('platinum')) {
      return this.standardGameNames.PLATINUM_BLITZ;
    } else if (name.includes('buffalo')) {
      return this.standardGameNames.BUFFALO_RUSH;
    } else if (name.includes('klassic_link')) {
      return this.standardGameNames.KLASSIC_LINK;
    } else if (name.includes('golden city')) {
      return this.standardGameNames.GOLDEN_CITY;
    } else if (name.includes('fortune ip')) {
      return this.standardGameNames.FORTUNE_IP;
    } else if (name.includes('fortune')) {
      return this.standardGameNames.FORTUNE;
    }
    return gameName;
  }

  private buildParams(
    startDate: Date,
    endDate: Date,
    salesReps: SalesRep[],
    gameName: string | undefined,
    boardAssetTag: string | undefined,
    groupByBoard: boolean
  ): HttpParams {
    let params = new HttpParams();
    
    params = params.set('startDate', formatLocalDateTime(startDate));
    params = params.set('endDate', formatLocalDateTime(endDate));

    // Handle sales reps array
    if (salesReps && salesReps.length > 0) {
      salesReps.forEach(rep => {
        if (rep.firstName) {
          params = params.set('salesRepFirstName', rep.firstName.trim());
        }
        if (rep.lastName) {
          params = params.set('salesRepLastName', rep.lastName.trim());
        }
      });
    }

    if (gameName && typeof gameName === 'string') {
      const standardizedGameName = this.standardizeGameName(gameName.trim());
      params = params.set('gameName', standardizedGameName);
    }

    if (boardAssetTag && typeof boardAssetTag === 'string') {
      params = params.set('boardAssetTag', boardAssetTag)
    }
    
    params = params.set('groupByBoard', groupByBoard.toString());
    params = params.set('excludeZeros', 'true');

    return params;
  }
  


  async getAllByDateRange(
    startDate: Date,
    endDate: Date,
    salesRepFirstName?: string,
    salesRepLastName?: string
  ): Promise<BoardStats[]> {
    // Use the updated GET_ALL_BY_DATE_RANGE_URL to construct the URL
    const url = GET_ALL_BY_DATE_RANGE_URL(startDate, endDate, salesRepFirstName, salesRepLastName);
  
    console.log('Fetching data from:', url);
  
    if (this.loginService.isloggedIn) {
      try {
        const response = await firstValueFrom(
          this.http.get<BoardStats[]>(url)
        );
        console.log('Data fetched:', response);
        return response;
      } catch (error) {
        console.error('Error fetching data:', error);
        return [];
      }
    } else {
      console.warn('User is not logged in');
      return [];
    }
  }

  async getDashboardSummary(startDate: Date, endDate: Date): Promise<DashboardBoardStatsSummary | null> {
    if (!this.loginService.isloggedIn) {
      console.warn('User is not logged in');
      return null;
    }

    const params = new HttpParams()
      .set('startDate', formatLocalDateTime(startDate))
      .set('endDate', formatLocalDateTime(endDate));

    try {
      return await firstValueFrom(
        this.http.get<DashboardBoardStatsSummary>(GET_DASHBOARD_SUMMARY_URL, { params })
      );
    } catch (error) {
      console.error('Error fetching dashboard board stats summary:', error);
      return null;
    }
  }

  async getDashboardCategorySummaries(
    startDate: Date,
    endDate: Date
  ): Promise<Record<string, DashboardBoardStatsSummary> | null> {
    if (!this.loginService.isloggedIn) return null;

    const params = new HttpParams()
      .set('startDate', formatLocalDateTime(startDate))
      .set('endDate', formatLocalDateTime(endDate));

    try {
      return await firstValueFrom(
        this.http.get<Record<string, DashboardBoardStatsSummary>>(
          GET_DASHBOARD_CATEGORY_SUMMARIES_URL,
          { params }
        )
      );
    } catch (error) {
      console.error('Error fetching dashboard category summaries:', error);
      return null;
    }
  }
  
  async getByBoardAssetTag(assetTag): Promise<[BoardStats]> {
    if (this.loginService.isloggedIn) {
      return await firstValueFrom(
        this.http.get<[BoardStats]>(GET_BY_ID_URL(assetTag))
      );
    }
  }

  groupStatsByBoardDetails(stats: BoardStats[], keyFunction: (detail: Board) => string): Map<string, GroupedStats> {
    const binned = new Map<string, GroupedStats>();
    for(let stat of stats) {
      const groupKey = keyFunction(stat.board);
      if (!binned.has(groupKey)) {
        // initialization
        binned.set(groupKey, {
          avgDailyCashIn: 0,
          avgDailyNet: 0,
          avgDailyCoinIn: 0,
          avgDailyCoinOut: 0,
          avgDailyGamePlayed: 0,
          avgDailyGameWon: 0,
          avgDays: 0,
          sumDailyCashIn: 0,
          sumDailyNet: 0,
          sumDailyCoinIn: 0,
          sumDailyCoinOut: 0,
          sumDailyGamePlayed: 0,
          sumDailyGameWon: 0,
          totalCashin: 0,
          totalCashout: 0,
          totalCoinIn: 0,
          totalCoinOut: 0,
          totalGamesPlayed: 0,
          totalGamesWon: 0,
          totalDays: 0,
          totalNet: 0,
          winRatio: 0,
          payoutRatio: 0,
          rpt: 0,
          betMultiplier: 0,
          betPerPlay: 0,  
          count: 0,
          rawStats: [] // <-- Add this
        });
      }

const val = binned.get(groupKey)!;

// ✅ Always push the stat to rawStats (for accurate scan count)
val.rawStats?.push(stat);

// ✅ Only aggregate if daysSincePrevRead is valid
if (!stat.manuallyFlagged && stat.daysSincePrevRead > 0) {
  val.sumDailyCashIn += stat.cashInDiff / stat.daysSincePrevRead;
  val.sumDailyNet += stat.profit / stat.daysSincePrevRead;
  val.sumDailyCoinIn += stat.coinInDiff / stat.daysSincePrevRead;
  val.sumDailyCoinOut += stat.coinOutDiff / stat.daysSincePrevRead;
  val.sumDailyGamePlayed += stat.gamePlayedDiff / stat.daysSincePrevRead;
  val.sumDailyGameWon += stat.gameWonDiff / stat.daysSincePrevRead;

  val.totalCashin += stat.cashInDiff;
  val.totalCashout += stat.cashOutDiff;
  val.totalCoinIn += stat.coinInDiff;
  val.totalCoinOut += stat.coinOutDiff;
  val.totalGamesPlayed += stat.gamePlayedDiff;
  val.totalGamesWon += stat.gameWonDiff;
  val.totalDays += stat.daysSincePrevRead;

  val.count += 1;
}
    }
    for(let binKey of binned.keys()) {
      const binData = binned.get(binKey);
      binData.avgDailyCashIn = binData.sumDailyCashIn / binData.count;
      binData.avgDailyNet = binData.sumDailyNet / binData.count;
      binData.avgDailyCoinIn = binData.sumDailyCoinIn / binData.count;
      binData.avgDailyCoinOut = binData.sumDailyCoinOut / binData.count;
      binData.avgDailyGamePlayed = binData.sumDailyGamePlayed / binData.count;
      binData.avgDailyGameWon = binData.sumDailyGameWon / binData.count;
      binData.winRatio = binData.totalGamesWon / binData.totalGamesPlayed || 0;
      binData.payoutRatio = binData.totalCashout / binData.totalCashin || 0;
      binData.rpt= binData.totalCoinOut / binData.totalCoinIn || 0;
      binData.betMultiplier = binData.sumDailyCoinIn / binData.sumDailyCashIn || 0;
      binData.betPerPlay = binData.sumDailyCoinIn / binData.sumDailyGamePlayed || 0;
      binData.totalNet = binData.totalCashin - binData.totalCashout || 0;
      binData.avgDays = binData.totalDays / binData.count;

    }
    return binned
  }

  standardGameKeyFunction(boardDetail: Board): string {
    const gameNameRaw = boardDetail?.installedGamePackage?.name || '';
    const gameName = gameNameRaw.toLowerCase();
    const compactGameName = gameName.replace(/[^a-z0-9]/g, '');

    // Match complete game families before generic words such as "classic" and
    // "fortune". Package names commonly arrive without spaces (and sometimes
    // with a "Classic" suffix), so substring order alone is not reliable.
    if (
      compactGameName.includes('worldofwonders') ||
      compactGameName.includes('worldsofwonder') ||
      compactGameName.includes('worldwonders') ||
      compactGameName.includes('megatreasure')
    ) {
      return 'WORLD OF WONDERS';
    } else if (
      compactGameName.includes('fortuneipthreeliner') ||
      compactGameName.includes('fortuneip3liner') ||
      compactGameName.includes('fortuneip3l')
    ) {
      return 'FORTUNE IP THREE LINER';
    } else if (
      compactGameName.includes('fortuneipfiveliner') ||
      compactGameName.includes('fortuneip5liner') ||
      compactGameName.includes('fortuneip5l')
    ) {
      return 'FORTUNE IP FIVE LINER';
    } else if (compactGameName.includes('fortuneone')) {
      return 'FORTUNE ONE';
    } else if (compactGameName.includes('realmsforriches')) {
      return 'REALMS FOR RICHES';
    } else if (
      compactGameName.includes('luckylabs') ||
      compactGameName.includes('luckylbas')
    ) {
      return 'LUCKY LABS';
    } else if (compactGameName.includes('klassiclink') || compactGameName.includes('classiclink')) {
      return 'KLASSIC LINK';
    } else if(gameName.includes('red')) {
      return 'RED BLAZE';
    } else if (gameName.includes('gold')) {
      return 'GOLD LINK'
    } else if (gameName.includes('classic') || gameName.includes('klassic')) {
      return 'KLASSIC KRAZE'
    } else if (gameName.includes('diamond')) {
      return 'DIAMOND BLAST LINK'
    } else if (gameName.includes('silver')) {
      return 'SILVER LINK'
    } else if (gameName.includes('platinum')) {
      return 'PLATINUM BLITZ'
    } else if (gameName.includes('buffalo')) {
      return 'BUFFALO RUSH'
    } else if (gameName.includes('golden city')) {
      return 'GOLDEN CITY'
    } else if (compactGameName.includes('fortuneip')) {
      return 'FORTUNE IP'
    } else if (gameName.includes('fortune')) {
      return 'FORTUNE';
    }
    console.log(`game ${gameNameRaw || 'Unknown'} produced 'unknown' as key`, {boardDetail, gameName})
    return gameNameRaw?.trim() ? gameNameRaw.toUpperCase() : 'Unknown';
  }

  async getLatestByBoardId(boardId: string): Promise<BoardStats | null> {
    if (!this.loginService.isloggedIn) {
      console.warn('User is not logged in');
      return null;
    }

    if (!boardId) {
      console.error('Board ID is required');
      return null;
    }

    try {
      const url = GET_LATEST_BOARD_ID_URL(boardId);
      console.log('Fetching latest board stats from:', url);
      
      const response = await firstValueFrom(
        this.http.get<BoardStats>(url)
      );

      if (!response) {
        console.log('No stats found for board:', boardId);
        return null;
      }

      return response;
    } catch (error: any) {
      if (error?.status === 404) {
        console.log('No stats found for board:', boardId);
        return null;
      }
      console.error('Error fetching board stats:', error);
      return null;
    }
  }
stateKeyFunction(board: Board): string {
  const location = board?.location ?? board?.currentDeployment?.location;
  const state = location?.businessAddress?.state;

  if (!state) {
    console.warn('Unknown state for board:', board);
  }

  return state || 'Unknown';
}

salesRepKeyFunction(board: Board): string {
  const rep = board?.currentDeployment?.location?.salesRep;
  if (!rep) return 'Unknown';
  return `${rep.firstName || ''} ${rep.lastName || ''}`.trim();
}

/** Delete exactly one board_stats row (and its board_game_stats) by serial+timestamp. 
 * Backend expects 'yyyy-MM-dd HH:mm:ss.SSSSSS' (note the space, not 'T').
 */
deleteOneRowBySerial(serialNumber: string, timeStamp: string) {
  if (!serialNumber || !timeStamp) {
    throw new Error('serialNumber and timeStamp are required');
  }

  // ✅ normalize to backend format *before* sending
  const ts = this.formatToBackendTs(timeStamp);

  const params = new HttpParams()
    .set('serialNumber', serialNumber.trim())
    .set('timeStamp', ts); // <-- exact key the backend consumes

  return this.http.delete<{ deletedBoardGameStats: number; deletedBoardStats: number }>(
    DELETE_ROW_BY_SERIAL_URL,
    { params }
  );
}

/** Utility: normalize various timestamp shapes to 'yyyy-MM-dd HH:mm:ss.SSSSSS' */
formatToBackendTs(ts: string | Date): string {
  if (!ts) return '';
  if (typeof ts === 'string') {
    // if it’s already 'yyyy-MM-dd HH:mm:ss.SSSSSS' or close, normalize spacing/length
    const s = ts.replace('T', ' ').replace('Z', '');
    // pad/truncate to microseconds (6)
    const [main, frac = ''] = s.split('.');
    const micro = (frac + '000000').slice(0, 6);
    return `${main}.${micro}`;
  }
  // Date -> ISO -> space, no Z, pad micros
  const iso = ts.toISOString().replace('T', ' ').replace('Z', '');
  const [main, frac = ''] = iso.split('.');
  const micro = (frac + '000000').slice(0, 6);
  return `${main}.${micro}`;
}  

flagStat(id: string, flagged: boolean, note?: string) {
  return this.http.post(`${BASE_URL}/${id}/flag`, {
    flagged,
    note
  });
}

updateCategory(id: string, category: 'STANDARD' | 'FUSION_LEASE' | 'PICKUP' | 'BOARD_SALES') {
  return this.http.patch<BoardStats>(`${BASE_URL}/${id}/category`, { category });
}

}
